import { Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { CustomModal } from "components/CustomModal";
import { Icon } from "components/Icon";
import { LinearProgressWithLabel } from "components/LinearProgressWithLabel";
import { useTranslation } from "react-i18next";

export type RoomBulkActionKind = "alignRoomType" | "assignRoomType" | "changeColor" | "deleteConfig" | "saveConfig";

type RoomBulkActionModalProps = {
  action: RoomBulkActionKind;
  count: number;
  isProcessing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  progress: number;
};

export const RoomBulkActionModal = ({
  action,
  count,
  isProcessing,
  onCancel,
  onConfirm,
  open,
  progress,
}: RoomBulkActionModalProps) => {
  const { t } = useTranslation();
  const actionContent: Record<RoomBulkActionKind, { button: string; confirm: string; progress: string }> = {
    alignRoomType: {
      button: t("TechnicalData.room.bulkAction.assign", "Assign"),
      confirm: t(
        "TechnicalData.room.bulkAction.confirmAlign",
        "Assign this room type to {{count}} room functions? They will use the existing room type's configuration.",
        { count }
      ),
      progress: t(
        "TechnicalData.room.bulkAction.aligning",
        "Assigning room type and applying the existing configuration to {{count}} room functions…",
        { count }
      ),
    },
    assignRoomType: {
      button: t("TechnicalData.room.bulkAction.assign", "Assign"),
      confirm: t(
        "TechnicalData.room.bulkAction.confirmAssign",
        "Assign this room type to {{count}} room functions?",
        { count }
      ),
      progress: t("TechnicalData.room.bulkAction.assigning", "Assigning room type to {{count}} room functions…", {
        count,
      }),
    },
    changeColor: {
      button: t("TechnicalData.room.bulkAction.change", "Change"),
      confirm: t("TechnicalData.room.bulkAction.confirmColor", "Change the color of {{count}} room functions?", {
        count,
      }),
      progress: t("TechnicalData.room.bulkAction.changingColor", "Changing color for {{count}} room functions…", {
        count,
      }),
    },
    deleteConfig: {
      button: t("general.delete", "Delete"),
      confirm: t(
        "TechnicalData.room.bulkAction.confirmDelete",
        "Delete the configurations of {{count}} room functions?",
        { count }
      ),
      progress: t(
        "TechnicalData.room.bulkAction.deletingConfig",
        "Deleting configurations from {{count}} room functions…",
        { count }
      ),
    },
    saveConfig: {
      button: t("general.save", "Save"),
      confirm: t(
        "TechnicalData.room.bulkAction.confirmSave",
        "Save the configuration for {{count}} room functions?",
        { count }
      ),
      progress: t(
        "TechnicalData.room.bulkAction.savingConfig",
        "Saving configuration for {{count}} room functions…",
        { count }
      ),
    },
  };
  const content = actionContent[action];

  return (
    <CustomModal
      closeIcon={!isProcessing}
      height="25rem"
      modalOpen={open}
      setModalOpen={(nextOpen: boolean) => {
        if (!nextOpen && !isProcessing) onCancel();
      }}
      width="44rem"
    >
      <Stack className="h-full w-full flex-col justify-between p-4 text-left">
        <div>
          <Stack className="flex-row items-center gap-3">
            <Icon color="var(--color-foreground-muted)" iconName="Warning" size="lg" />
            <h3 className="font-ds-headline-md">{isProcessing ? content.progress : content.confirm}</h3>
          </Stack>
          {isProcessing && (
            <Stack className="mt-8 gap-2">
              <LinearProgressWithLabel fullWidth hasAnimation value={progress} />
              <span className="font-ds-body-regular-sm text-lds-foreground-muted">
                {t("TechnicalData.room.bulkAction.progress", "{{completed}}% complete", {
                  completed: Math.round(progress),
                })}
              </span>
            </Stack>
          )}
        </div>
        {!isProcessing && (
          <Stack className="flex-row gap-4">
            <Button onClick={onConfirm} size="md" variant={action === "deleteConfig" ? "error" : "primary"}>
              {content.button}
            </Button>
            <Button onClick={onCancel} size="md" variant="secondary">
              {t("general.cancel")}
            </Button>
          </Stack>
        )}
      </Stack>
    </CustomModal>
  );
};
